import * as z from "zod";

/** HrokBot Connect v1: host access is separate from the server-side Computer. */
export const CONNECT_API_PREFIX = "/api/connect/v1";
export const CONNECT_PROTOCOL_VERSION = 1;
export const CONNECT_POLL_INTERVAL_MS = 1_000;
export const CONNECT_LEASE_MS = 5_000;
export const CONNECT_PAIRING_TTL_MS = 10 * 60_000;
export const CONNECT_MAX_FILE_BYTES = 1_048_576;
export const CONNECT_MAX_RESULT_BYTES = 3 * 1_048_576;
export const ConnectIdSchema = z.string().min(1).max(128);
const timestamp = z.string().datetime();
const epoch = z.number().int().nonnegative();
const unixAbsolutePath = /^\//;
const windowsAbsolutePath = /^(?:[A-Za-z]:[\\/]|\\\\[^\\/]+[\\/][^\\/]+)/;
const absolutePath = z
  .string()
  .min(1)
  .max(4096)
  .refine((p) => unixAbsolutePath.test(p) || windowsAbsolutePath.test(p), "Absolute path required")
  .refine((p) => !p.includes("\0"), "Invalid path");
/** Tool categories are not an OS sandbox: process execution and GUI control can indirectly access files/network. */
export const ConnectCapabilitySchema = z.enum([
  "files.read",
  "files.write",
  "process.execute",
  "screen.capture",
  "screen.control",
  "network",
]);
export type ConnectCapability = z.infer<typeof ConnectCapabilitySchema>;
export const ConnectScopeSchema = z.enum(["account", "space"]);
export type ConnectScope = z.infer<typeof ConnectScopeSchema>;
export const ConnectPolicySchema = z
  .object({
    access: z.enum(["all", "selected", "none"]),
    botIds: z.array(ConnectIdSchema).max(1000),
    capabilities: z.array(ConnectCapabilitySchema).max(6),
  })
  .strict();
export type ConnectPolicy = z.infer<typeof ConnectPolicySchema>;
export const ConnectActivitySchema = z.enum(["idle", "files", "network", "screen", "process"]);
export type ConnectActivity = z.infer<typeof ConnectActivitySchema>;
export const ConnectActionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("files.list"), path: absolutePath }).strict(),
  z.object({ kind: z.literal("files.stat"), path: absolutePath }).strict(),
  z
    .object({
      kind: z.literal("files.read"),
      path: absolutePath,
      maxBytes: z.number().int().min(1).max(CONNECT_MAX_FILE_BYTES).optional(),
      offset: z
        .number()
        .int()
        .min(0)
        .max(4 * 1024 * 1024 * 1024)
        .optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("files.write"),
      path: absolutePath,
      contentBase64: z
        .string()
        .max(Math.ceil(CONNECT_MAX_FILE_BYTES / 3) * 4)
        .regex(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/),
      overwrite: z.boolean(),
      offset: z
        .number()
        .int()
        .min(0)
        .max(256 * 1024 * 1024)
        .optional(),
      append: z.boolean().optional(),
    })
    .strict(),
  z.object({ kind: z.literal("files.mkdir"), path: absolutePath }).strict(),
  z
    .object({ kind: z.literal("files.remove"), path: absolutePath, recursive: z.boolean() })
    .strict(),
  z
    .object({
      kind: z.literal("files.move"),
      from: absolutePath,
      to: absolutePath,
      overwrite: z.boolean(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("files.copy"),
      from: absolutePath,
      to: absolutePath,
      overwrite: z.boolean(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("files.glob"),
      path: absolutePath,
      pattern: z
        .string()
        .min(1)
        .max(256)
        .refine(
          (value) => !value.includes("/") && !value.includes("\0"),
          "File name glob required",
        ),
      limit: z.number().int().min(1).max(2000).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("process.exec"),
      executable: absolutePath,
      args: z
        .array(
          z
            .string()
            .max(8192)
            .refine((s) => !s.includes("\0")),
        )
        .max(128),
      cwd: absolutePath.optional(),
      timeoutMs: z.number().int().min(1).max(300_000),
    })
    .strict(),
  z
    .object({
      kind: z.literal("process.start"),
      executable: absolutePath,
      args: z
        .array(
          z
            .string()
            .max(8192)
            .refine((s) => !s.includes("\0")),
        )
        .max(128),
      cwd: absolutePath.optional(),
      label: z.string().max(80).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("process.output"),
      jobId: ConnectIdSchema,
      offset: z
        .number()
        .int()
        .min(0)
        .max(4 * 1024 * 1024 * 1024)
        .optional(),
      maxBytes: z.number().int().min(1).max(CONNECT_MAX_FILE_BYTES).optional(),
    })
    .strict(),
  z.object({ kind: z.literal("process.stop"), jobId: ConnectIdSchema }).strict(),
  z.object({ kind: z.literal("process.list") }).strict(),
  z
    .object({
      kind: z.literal("network.fetch"),
      url: z
        .string()
        .max(8192)
        .url()
        .refine((value) => {
          try {
            const url = new URL(value);
            return url.protocol === "https:" && !url.username && !url.password;
          } catch {
            return false;
          }
        }, "HTTPS URL without credentials required"),
      method: z.literal("GET"),
      timeoutMs: z.number().int().min(1).max(10_000),
    })
    .strict(),
  z.object({ kind: z.literal("screen.capture") }).strict(),
  z
    .object({
      kind: z.literal("screen.click"),
      x: z.number().int().min(-32768).max(32768),
      y: z.number().int().min(-32768).max(32768),
    })
    .strict(),
  z
    .object({
      kind: z.literal("screen.move"),
      x: z.number().int().min(-32768).max(32768),
      y: z.number().int().min(-32768).max(32768),
    })
    .strict(),
  z
    .object({
      kind: z.literal("screen.scroll"),
      deltaX: z.number().int().min(-1000).max(1000),
      deltaY: z.number().int().min(-1000).max(1000),
    })
    .strict(),
  z.object({ kind: z.literal("screen.type"), text: z.string().max(16_384) }).strict(),
  z
    .object({
      kind: z.literal("screen.key"),
      key: z.enum([
        "return",
        "tab",
        "escape",
        "space",
        "backspace",
        "delete",
        "up",
        "down",
        "left",
        "right",
        "home",
        "end",
        "pageup",
        "pagedown",
        ..."abcdefghijklmnopqrstuvwxyz0123456789",
      ]),
      modifiers: z.array(z.enum(["command", "shift", "option", "control"])).max(4),
    })
    .strict(),
  z
    .object({
      kind: z.literal("ui.tree"),
      app: z.string().min(1).max(120),
      maxDepth: z.number().int().min(1).max(8).optional(),
      maxNodes: z.number().int().min(1).max(2000).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("ui.perform"),
      app: z.string().min(1).max(120),
      id: z
        .string()
        .min(1)
        .max(256)
        .regex(/^\d+(?:\/\d+)*$/),
      action: z.enum(["press", "focus", "setValue", "confirm"]),
      value: z.string().max(4096).optional(),
    })
    .strict(),
]);
export type ConnectAction = z.infer<typeof ConnectActionSchema>;
export const ConnectCommandSchema = z
  .object({
    id: ConnectIdSchema,
    deviceId: ConnectIdSchema,
    sessionId: ConnectIdSchema,
    epoch,
    botId: ConnectIdSchema,
    expiresAt: timestamp,
    action: ConnectActionSchema,
  })
  .strict();
export type ConnectCommand = z.infer<typeof ConnectCommandSchema>;
export const ConnectPlatformSchema = z.enum(["darwin", "linux", "windows"]);
export type ConnectPlatform = z.infer<typeof ConnectPlatformSchema>;
export const ConnectPairingStartSchema = z
  .object({
    installationId: ConnectIdSchema,
    deviceName: z.string().trim().min(1).max(80),
    platform: ConnectPlatformSchema,
  })
  .strict();
export const ConnectPairingStartedSchema = z
  .object({
    pairingId: ConnectIdSchema,
    pairingToken: z.string().min(32).max(256),
    userCode: z.string().min(6).max(16),
    verificationUrl: z.string().url(),
    expiresAt: timestamp,
  })
  .strict();
export const ConnectPairingIdSchema = z.object({ pairingId: ConnectIdSchema }).strict();
export const ConnectPairingStatusSchema = z
  .object({
    state: z.enum(["pending", "claimed", "expired"]),
    scope: ConnectScopeSchema.default("account"),
    owner: z
      .object({
        ownerId: ConnectIdSchema,
        spaceId: ConnectIdSchema,
        displayName: z.string().max(256),
        spaceName: z.string().max(256),
      })
      .strict()
      .optional(),
    policy: ConnectPolicySchema.optional(),
  })
  .strict();
export const ConnectPairedSchema = z
  .object({ deviceId: ConnectIdSchema, deviceToken: z.string().min(32).max(256), epoch })
  .strict();
export const ConnectDeviceIdSchema = z.object({ deviceId: ConnectIdSchema }).strict();
export const ConnectSessionSchema = z.object({ sessionId: ConnectIdSchema, epoch }).strict();
export const ConnectSessionIdentitySchema = z
  .object({ deviceId: ConnectIdSchema, sessionId: ConnectIdSchema, epoch })
  .strict();
/** Advisory device-reported readiness, never server authorization. Absence means unknown. */
export const ConnectLocalAccessSchema = z
  .object({
    roots: z.array(absolutePath).max(100),
    fullControl: z.boolean(),
    screen: z.boolean(),
  })
  .strict();
export type ConnectLocalAccess = z.infer<typeof ConnectLocalAccessSchema>;
export const ConnectPollSchema = ConnectSessionIdentitySchema.extend({
  activity: ConnectActivitySchema,
  localAccess: ConnectLocalAccessSchema.optional(),
  permissionStatus: z
    .record(z.string().max(64), z.string().max(64))
    .refine((value) => Object.keys(value).length <= 32, "Too many permission entries"),
});
/**
 * Restart or shut down the computer, or install the newest published HrokBot Connect on it. Only
 * the owner asks for it, from the web app; bots never can. The device may refuse (paused on the
 * device, or power control switched off there). "update" goes only to a device that said it can.
 */
export const ConnectPowerActionSchema = z.enum(["restart", "shutdown", "update"]);
export type ConnectPowerAction = z.infer<typeof ConnectPowerActionSchema>;
/** How long a power request waits for the device to pick it up. */
export const CONNECT_POWER_REQUEST_MS = 2 * 60_000;
export const ConnectPowerRequestSchema = z
  .object({ id: ConnectIdSchema, action: ConnectPowerActionSchema, expiresAt: timestamp })
  .strict();
export type ConnectPowerRequest = z.infer<typeof ConnectPowerRequestSchema>;
export const ConnectPollResponseSchema = z
  .object({
    state: z.enum(["active", "paused", "revoked"]),
    epoch,
    leaseValidUntil: timestamp,
    commands: z.array(ConnectCommandSchema).max(8),
    // Older clients ignore it; a request reaches only the current session.
    power: ConnectPowerRequestSchema.optional(),
  })
  .strict();
export type ConnectPollResponse = z.infer<typeof ConnectPollResponseSchema>;
export const ConnectStartSchema = ConnectSessionIdentitySchema.extend({
  commandId: ConnectIdSchema,
});
export const ConnectStartResponseSchema = z
  .object({ allowed: z.boolean(), leaseValidUntil: timestamp })
  .strict();
export const ConnectPauseSchema = ConnectDeviceIdSchema.extend({ paused: z.boolean() });
const connectVersion = z.string().regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}$/);
/**
 * What the device runs. Sent on its own route rather than with each poll, so a newer client still
 * polls an older server, which answers this route with 404.
 */
export const ConnectClientReportSchema = ConnectSessionIdentitySchema.extend({
  version: connectVersion,
  /** A newer published version the device found, if any. */
  latest: connectVersion.optional(),
  /** The device can install a newer version by itself when the owner asks. */
  canUpdate: z.boolean(),
});
export type ConnectClientReport = z.infer<typeof ConnectClientReportSchema>;
/** The device's answer to a power request, sent before the system goes down when it can be. */
export const ConnectPowerResultSchema = ConnectSessionIdentitySchema.extend({
  powerId: ConnectIdSchema,
  status: z.enum(["started", "refused", "failed"]),
  error: z.string().max(512).optional(),
});
export type ConnectPowerResult = z.infer<typeof ConnectPowerResultSchema>;
export const ConnectResultSchema = ConnectStartSchema.extend({
  status: z.enum(["succeeded", "failed", "blocked", "cancelled"]),
  output: z.unknown().optional(),
  error: z.string().max(4096).optional(),
});
export type ConnectResult = z.infer<typeof ConnectResultSchema>;
export const ConnectOwnerListSchema = z.object({ spaceId: ConnectIdSchema }).strict();
const ConnectBotOptionSchema = z
  .object({ id: ConnectIdSchema, name: z.string().max(256) })
  .strict();
export const ConnectOwnerOptionsSchema = z
  .object({
    canManageSpace: z.boolean(),
    bots: z.array(ConnectBotOptionSchema).max(1000),
    spaceBots: z.array(ConnectBotOptionSchema).max(1000),
  })
  .strict();
export const ConnectOwnerClaimSchema = ConnectOwnerListSchema.extend({
  userCode: z.string().min(6).max(16),
  scope: ConnectScopeSchema.default("account"),
  policy: ConnectPolicySchema,
});
export const ConnectOwnerDeviceSchema = ConnectOwnerListSchema.extend({
  deviceId: ConnectIdSchema,
});
export const ConnectOwnerPauseSchema = ConnectOwnerDeviceSchema.extend({ paused: z.boolean() });
export const ConnectOwnerPolicySchema = ConnectOwnerDeviceSchema.extend({
  policy: ConnectPolicySchema,
});
export const ConnectOwnerPowerSchema = ConnectOwnerDeviceSchema.extend({
  action: ConnectPowerActionSchema,
});
export const ConnectDeviceSummarySchema = z
  .object({
    id: ConnectIdSchema,
    scope: ConnectScopeSchema.default("account"),
    ownerLabel: z.string().max(256).optional(),
    deviceName: z.string().max(80),
    platform: ConnectPlatformSchema,
    state: z.enum(["active", "paused", "revoked", "offline"]),
    ownerPaused: z.boolean().optional(),
    localPaused: z.boolean().optional(),
    localAccess: ConnectLocalAccessSchema.optional(),
    policy: ConnectPolicySchema,
    epoch,
    lastSeenAt: timestamp.nullable(),
    activity: ConnectActivitySchema,
    permissionStatus: z.record(z.string(), z.string()),
    /** What the device last reported it runs; absent for a client too old to say. */
    client: z
      .object({
        version: connectVersion,
        latest: connectVersion.optional(),
        canUpdate: z.boolean(),
      })
      .strict()
      .optional(),
    /** The latest power request: waiting for the device, or what the device answered. */
    power: z
      .object({
        action: ConnectPowerActionSchema,
        status: z.enum(["requested", "started", "refused", "failed", "expired"]),
        requestedAt: timestamp,
        error: z.string().max(512).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type ConnectDeviceSummary = z.infer<typeof ConnectDeviceSummarySchema>;

/** Required permission, not sufficient authorization: owner scope, epoch, lease and bot membership must also hold. */
export function connectActionCapability(action: ConnectAction): ConnectCapability {
  if (
    action.kind === "files.list" ||
    action.kind === "files.stat" ||
    action.kind === "files.read" ||
    action.kind === "files.glob"
  )
    return "files.read";
  if (action.kind.startsWith("files.")) return "files.write";
  if (action.kind.startsWith("process.")) return "process.execute";
  if (action.kind === "network.fetch") return "network";
  return action.kind === "screen.capture" ? "screen.capture" : "screen.control";
}
export function connectPolicyAllows(
  policy: ConnectPolicy,
  botId: string,
  action: ConnectAction,
): boolean {
  return (
    policy.access !== "none" &&
    (policy.access === "all" || policy.botIds.includes(botId)) &&
    policy.capabilities.includes(connectActionCapability(action))
  );
}
/** Conservatively serialize arbitrary process execution; cannot infer its side effects. */
export function connectActionsConflict(a: ConnectAction, b: ConnectAction): boolean {
  if (a.kind.startsWith("process.") || b.kind.startsWith("process.")) return true;
  const screen = (action: ConnectAction) =>
    action.kind.startsWith("screen.") || action.kind.startsWith("ui.");
  if (screen(a) && screen(b)) return true;
  const files = (action: ConnectAction) => action.kind.startsWith("files.");
  const read = (action: ConnectAction) =>
    action.kind === "files.read" ||
    action.kind === "files.list" ||
    action.kind === "files.stat" ||
    action.kind === "files.glob";
  return files(a) && files(b) && !(read(a) && read(b));
}
/** Pre-execution fence only. Once started, the renewable execution lease—not this dispatch deadline—governs cancellation. */
export function connectCommandIsCurrent(
  command: ConnectCommand,
  identity: { deviceId: string; sessionId: string; epoch: number },
  now: number,
): boolean {
  return (
    command.deviceId === identity.deviceId &&
    command.sessionId === identity.sessionId &&
    command.epoch === identity.epoch &&
    Date.parse(command.expiresAt) > now
  );
}
